import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Download,
  ExternalLink,
  FilePlus2,
  History,
  ImageIcon,
  Plus,
  RadioTower,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Link } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import type { CrmDataProvider } from "../providers/types";
import type {
  DashboardCapabilities,
  WhatsAppTemplate,
  WhatsAppTemplateInput,
  WhatsAppTemplateLibraryBodyInputs,
  WhatsAppTemplateLibraryButtonInput,
  WhatsAppTemplateLibraryFilters,
  WhatsAppTemplateLibraryImport,
  WhatsAppTemplateLibraryImportInput,
  WhatsAppTemplateLibraryItem,
  WhatsAppTemplateMediaAsset,
} from "../types";
import { BUTTON_LABEL_PRESETS } from "./botButtonIntents";
import { ButtonIntentHint } from "./ButtonIntentHint";
import { WhatsAppMessageBubble } from "./WhatsAppMessageBubble";

const emptyDraft: WhatsAppTemplateInput = {
  name: "",
  language: "en_US",
  category: "UTILITY",
  body_text: "",
  header_format: "NONE",
  header_text: "",
  footer_text: "",
};

type LibraryButtonInputDraft = {
  type: string;
  text?: string;
  phoneNumber?: string;
  url?: string;
  otpType?: string;
  zeroTapTermsAccepted?: boolean;
};

type TemplateButtonDraft = {
  type: "QUICK_REPLY" | "URL";
  text: string;
  url?: string;
};

export function WhatsAppTemplatesPage() {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [capabilities, setCapabilities] =
    useState<DashboardCapabilities | null>(null);
  const [capabilityError, setCapabilityError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<WhatsAppTemplateInput>(emptyDraft);
  const [appealTemplate, setAppealTemplate] = useState<WhatsAppTemplate | null>(
    null,
  );
  const [appealNote, setAppealNote] = useState("");
  const [tab, setTab] = useState("templates");
  const [libraryItems, setLibraryItems] = useState<
    WhatsAppTemplateLibraryItem[]
  >([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryCursor, setLibraryCursor] = useState<string | null>(null);
  const [libraryFilters, setLibraryFilters] =
    useState<WhatsAppTemplateLibraryFilters>({
      search: "",
      language: "en_US",
      limit: 25,
    });
  const [libraryImportTemplate, setLibraryImportTemplate] =
    useState<WhatsAppTemplateLibraryItem | null>(null);
  const [templateImports, setTemplateImports] = useState<
    WhatsAppTemplateLibraryImport[]
  >([]);
  const [importsLoading, setImportsLoading] = useState(false);

  const approvedCount = useMemo(
    () => templates.filter((template) => template.status === "approved").length,
    [templates],
  );
  const featureFlags = capabilities?.features ?? {};
  const templatesEnabled = featureFlags.whatsapp_templates !== false;
  const catalogSyncEnabled = Boolean(featureFlags.template_catalog_sync);
  const mediaUploadEnabled = Boolean(featureFlags.template_media_upload);
  const templateLibraryEnabled = Boolean(featureFlags.template_library);
  const templateLibraryImportEnabled = Boolean(
    featureFlags.template_library_import,
  );

  const loadCapabilities = useCallback(async () => {
    try {
      setCapabilities(await dataProvider.getDashboardCapabilities());
      setCapabilityError(null);
    } catch (error) {
      setCapabilityError(errorMessage(error));
    }
  }, [dataProvider]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await dataProvider.listWhatsAppTemplates());
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify]);

  const loadLibrary = useCallback(
    async (cursor?: string | null) => {
      if (!templateLibraryEnabled) return;
      setLibraryLoading(true);
      try {
        const page = await dataProvider.listWhatsAppTemplateLibrary({
          ...libraryFilters,
          cursor: cursor ?? undefined,
        });
        setLibraryItems((current) =>
          cursor ? [...current, ...page.data] : page.data,
        );
        setLibraryCursor(page.next_cursor ?? null);
      } catch (error) {
        notify(errorMessage(error), { type: "error" });
      } finally {
        setLibraryLoading(false);
      }
    },
    [dataProvider, libraryFilters, notify, templateLibraryEnabled],
  );

  const loadImports = useCallback(async () => {
    if (!templateLibraryImportEnabled) return;
    setImportsLoading(true);
    try {
      setTemplateImports(
        await dataProvider.listWhatsAppTemplateLibraryImports(),
      );
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    } finally {
      setImportsLoading(false);
    }
  }, [dataProvider, notify, templateLibraryImportEnabled]);

  useEffect(() => {
    void loadCapabilities();
    void load();
  }, [load, loadCapabilities]);

  useEffect(() => {
    if (tab === "library" && templateLibraryEnabled && !libraryItems.length) {
      void loadLibrary(null);
    }
    if (tab === "imports" && templateLibraryImportEnabled) {
      void loadImports();
    }
  }, [
    libraryItems.length,
    loadImports,
    loadLibrary,
    tab,
    templateLibraryEnabled,
    templateLibraryImportEnabled,
  ]);

  const saveTemplate = async (submit: boolean) => {
    try {
      await dataProvider.createWhatsAppTemplate({
        ...draft,
        buttons: cleanTemplateButtons(draft.buttons),
        header_format:
          draft.header_format === "NONE" ? undefined : draft.header_format,
        submit,
      });
      notify(
        submit
          ? "crm.whatsapp.templates.submitted"
          : "crm.whatsapp.templates.saved",
        { type: "success" },
      );
      setOpen(false);
      setDraft(emptyDraft);
      await load();
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  const syncAllTemplates = async () => {
    try {
      await dataProvider.syncAllWhatsAppTemplates();
      notify("crm.whatsapp.templates.sync_all_done", { type: "success" });
      await load();
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  const uploadTemplateMedia = async (
    file: File,
  ): Promise<WhatsAppTemplateMediaAsset> => {
    const dataBase64 = await fileToBase64(file);
    return dataProvider.uploadWhatsAppTemplateMedia({
      file_name: file.name,
      mime_type: file.type || "application/octet-stream",
      data_base64: dataBase64,
      operator_user_id: identity?.id,
      operator_name: identity?.fullName,
    });
  };

  const syncTemplate = async (template: WhatsAppTemplate) => {
    try {
      await dataProvider.syncWhatsAppTemplate(template.id);
      notify("crm.whatsapp.templates.synced", { type: "success" });
      await load();
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  const unpauseTemplate = async (template: WhatsAppTemplate) => {
    try {
      await dataProvider.unpauseWhatsAppTemplate(template.id);
      notify("crm.whatsapp.templates.unpaused", { type: "success" });
      await load();
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  const submitAppealNote = async () => {
    if (!appealTemplate || !appealNote.trim()) return;
    try {
      await dataProvider.addWhatsAppTemplateAppealNote(
        appealTemplate.id,
        appealNote.trim(),
        { id: identity?.id, fullName: identity?.fullName },
      );
      notify("crm.whatsapp.templates.appeal_saved", { type: "success" });
      setAppealTemplate(null);
      setAppealNote("");
      await load();
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  const importLibraryTemplate = async (
    payload: WhatsAppTemplateLibraryImportInput,
  ) => {
    try {
      await dataProvider.importWhatsAppTemplateFromLibrary({
        ...payload,
        operator_user_id: identity?.id,
        operator_name: identity?.fullName,
      });
      notify("crm.whatsapp.templates.library_imported", { type: "success" });
      setLibraryImportTemplate(null);
      setTab("templates");
      await load();
      await loadImports();
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  const syncImport = async (record: WhatsAppTemplateLibraryImport) => {
    try {
      await dataProvider.syncWhatsAppTemplateLibraryImport(record.id);
      notify("crm.whatsapp.templates.import_synced", { type: "success" });
      await Promise.all([load(), loadImports()]);
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">
            {translate("crm.whatsapp.templates.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {translate("crm.whatsapp.templates.summary", {
              approved: approvedCount,
              total: templates.length,
            })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/whatsapp_broadcasts">
              <RadioTower className="size-4" />
              {translate("crm.whatsapp.broadcasts.title")}
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw className="size-4" />
            {translate("ra.action.refresh")}
          </Button>
          <Button
            variant="outline"
            onClick={() => void syncAllTemplates()}
            disabled={loading || !templatesEnabled || !catalogSyncEnabled}
          >
            <RefreshCw className="size-4" />
            {translate("crm.whatsapp.templates.sync_all")}
          </Button>
          <Button onClick={() => setOpen(true)} disabled={!templatesEnabled}>
            <FilePlus2 className="size-4" />
            {translate("crm.whatsapp.templates.new")}
          </Button>
        </div>
      </div>

      <Alert>
        <CheckCircle2 className="size-4" />
        <AlertTitle>
          {translate("crm.whatsapp.templates.policy_title")}
        </AlertTitle>
        <AlertDescription>
          {translate("crm.whatsapp.templates.policy_note")}
        </AlertDescription>
      </Alert>

      {(capabilityError || !templatesEnabled) && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertTitle>
            {translate("crm.whatsapp.capabilities.templates_unavailable")}
          </AlertTitle>
          <AlertDescription>
            {capabilityError ||
              translate("crm.whatsapp.capabilities.templates_disabled")}
          </AlertDescription>
        </Alert>
      )}

      {templatesEnabled && capabilities && !catalogSyncEnabled && (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertTitle>
            {translate("crm.whatsapp.capabilities.sync_unavailable")}
          </AlertTitle>
          <AlertDescription>
            {translate("crm.whatsapp.capabilities.sync_disabled")}
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <TabsList>
          <TabsTrigger value="templates">
            <CheckCircle2 className="size-4" />
            {translate("crm.whatsapp.templates.my_templates")}
          </TabsTrigger>
          <TabsTrigger value="library" disabled={!templateLibraryEnabled}>
            <BookOpen className="size-4" />
            {translate("crm.whatsapp.templates.library")}
          </TabsTrigger>
          <TabsTrigger value="imports" disabled={!templateLibraryImportEnabled}>
            <History className="size-4" />
            {translate("crm.whatsapp.templates.import_history")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {translate("crm.whatsapp.templates.name")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.category")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.status")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.sender")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.quality")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.updated")}
                  </TableHead>
                  <TableHead className="text-right">
                    {translate("crm.whatsapp.templates.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={String(template.id)}>
                    <TableCell>
                      <div className="font-medium">{template.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {template.language}
                      </div>
                      {template.library_template_name && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {translate("crm.whatsapp.templates.imported_from", {
                            name: template.library_template_name,
                          })}
                        </div>
                      )}
                      {template.body_text && (
                        <div className="mt-1 max-w-md truncate text-xs text-muted-foreground">
                          {template.body_text}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{template.category}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(template.status)}>
                        {template.status}
                      </Badge>
                      {templateRejectionReason(template) && (
                        <div className="mt-1 max-w-xs truncate text-xs text-destructive">
                          {templateRejectionReason(template)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {template.sender_environment || "-"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {shortSenderId(
                          template.provider_waba_id || template.waba_id,
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{template.quality_score || "-"}</TableCell>
                    <TableCell>{formatDate(template.updated_at)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void syncTemplate(template)}
                        >
                          <RefreshCw className="size-4" />
                          {translate("crm.whatsapp.templates.sync")}
                        </Button>
                        {template.status === "paused" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void unpauseTemplate(template)}
                          >
                            <RotateCcw className="size-4" />
                            {translate("crm.whatsapp.templates.unpause")}
                          </Button>
                        )}
                        {template.status === "rejected" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAppealTemplate(template)}
                          >
                            {translate("crm.whatsapp.templates.appeal")}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!templates.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      {loading
                        ? translate("crm.common.loading")
                        : translate("crm.whatsapp.templates.empty")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="library" className="space-y-4">
          {!templateLibraryEnabled && (
            <Alert>
              <AlertTriangle className="size-4" />
              <AlertTitle>
                {translate("crm.whatsapp.capabilities.library_unavailable")}
              </AlertTitle>
              <AlertDescription>
                {translate("crm.whatsapp.capabilities.library_disabled")}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-3 rounded-md border p-3 lg:grid-cols-[1fr_9rem_9rem_9rem_auto]">
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.templates.search")}</Label>
              <Input
                value={libraryFilters.search ?? ""}
                onChange={(event) =>
                  setLibraryFilters((current) => ({
                    ...current,
                    search: event.target.value,
                  }))
                }
                placeholder={translate(
                  "crm.whatsapp.templates.search_placeholder",
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.templates.language")}</Label>
              <Input
                value={libraryFilters.language ?? ""}
                onChange={(event) =>
                  setLibraryFilters((current) => ({
                    ...current,
                    language: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.templates.topic")}</Label>
              <Input
                value={libraryFilters.topic ?? ""}
                onChange={(event) =>
                  setLibraryFilters((current) => ({
                    ...current,
                    topic: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.templates.usecase")}</Label>
              <Input
                value={libraryFilters.usecase ?? ""}
                onChange={(event) =>
                  setLibraryFilters((current) => ({
                    ...current,
                    usecase: event.target.value,
                  }))
                }
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => void loadLibrary(null)}
                disabled={!templateLibraryEnabled || libraryLoading}
              >
                <Search className="size-4" />
                {translate("crm.whatsapp.templates.search")}
              </Button>
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {translate("crm.whatsapp.templates.name")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.category")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.topic")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.preview")}
                  </TableHead>
                  <TableHead className="text-right">
                    {translate("crm.whatsapp.templates.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {libraryItems.map((item) => (
                  <TableRow key={String(item.id)}>
                    <TableCell>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.language}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {renderIndustry(item.industry)}
                      </div>
                    </TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>
                      <div>{item.topic || "-"}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.usecase || "-"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xl whitespace-pre-wrap text-sm">
                        {item.body_text || "-"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => setLibraryImportTemplate(item)}
                        >
                          <Download className="size-4" />
                          {translate("crm.whatsapp.templates.import")}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!libraryItems.length && (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      {libraryLoading
                        ? translate("crm.common.loading")
                        : translate("crm.whatsapp.templates.library_empty")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {libraryCursor && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => void loadLibrary(libraryCursor)}
                disabled={libraryLoading}
              >
                {translate("ra.navigation.next")}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="imports" className="space-y-4">
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => void loadImports()}
              disabled={!templateLibraryImportEnabled || importsLoading}
            >
              <RefreshCw className="size-4" />
              {translate("ra.action.refresh")}
            </Button>
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {translate("crm.whatsapp.templates.name")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.imported_from_label")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.status")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.operator")}
                  </TableHead>
                  <TableHead>
                    {translate("crm.whatsapp.templates.updated")}
                  </TableHead>
                  <TableHead className="text-right">
                    {translate("crm.whatsapp.templates.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templateImports.map((record) => (
                  <TableRow key={String(record.id)}>
                    <TableCell>
                      <div className="font-medium">{record.template_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {record.template_language}
                      </div>
                    </TableCell>
                    <TableCell>{record.library_template_name}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(record.template_status)}>
                        {record.template_status}
                      </Badge>
                      {record.sync_status && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {record.sync_status}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>{record.operator_name || "-"}</div>
                      {record.operator_identity_status === "missing" && (
                        <div className="text-xs text-destructive">
                          {translate("crm.whatsapp.templates.operator_missing")}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(record.updated_at ?? record.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void syncImport(record)}
                        >
                          <RefreshCw className="size-4" />
                          {translate("crm.whatsapp.templates.sync_status")}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!templateImports.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      {importsLoading
                        ? translate("crm.common.loading")
                        : translate(
                            "crm.whatsapp.templates.import_history_empty",
                          )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <TemplateDialog
        open={open}
        draft={draft}
        onOpenChange={setOpen}
        onChange={setDraft}
        onSave={saveTemplate}
        onUploadMedia={uploadTemplateMedia}
        mediaUploadEnabled={mediaUploadEnabled}
      />

      <LibraryImportDialog
        item={libraryImportTemplate}
        existingTemplates={templates}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setLibraryImportTemplate(null);
        }}
        onImport={importLibraryTemplate}
      />

      <Dialog
        open={!!appealTemplate}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setAppealTemplate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("crm.whatsapp.templates.appeal_title")}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            value={appealNote}
            onChange={(event) => setAppealNote(event.target.value)}
            rows={5}
            placeholder={translate("crm.whatsapp.templates.appeal_placeholder")}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppealTemplate(null)}>
              {translate("ra.action.cancel")}
            </Button>
            <Button onClick={() => void submitAppealNote()}>
              {translate("crm.whatsapp.templates.save_appeal")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LibraryImportDialog({
  item,
  existingTemplates,
  onOpenChange,
  onImport,
}: {
  item: WhatsAppTemplateLibraryItem | null;
  existingTemplates: WhatsAppTemplate[];
  onOpenChange: (open: boolean) => void;
  onImport: (payload: WhatsAppTemplateLibraryImportInput) => Promise<void>;
}) {
  const translate = useTranslate();
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [category, setCategory] = useState("UTILITY");
  const [bodyInputs, setBodyInputs] =
    useState<WhatsAppTemplateLibraryBodyInputs>({});
  const [buttonInputs, setButtonInputs] = useState<LibraryButtonInputDraft[]>(
    [],
  );
  const [importing, setImporting] = useState(false);
  const duplicateTemplate = existingTemplates.find(
    (template) =>
      template.name === normalizeTemplateName(templateName) &&
      template.language === language.trim(),
  );

  useEffect(() => {
    if (!item) return;
    setTemplateName(makeTemplateNameSuggestion(item, existingTemplates));
    setLanguage(item.language || "en_US");
    setCategory(item.category || "UTILITY");
    setBodyInputs({});
    setButtonInputs(initialLibraryButtonDrafts(item.buttons));
  }, [existingTemplates, item]);

  const submit = async () => {
    if (!item || !templateName.trim() || !language.trim()) return;
    setImporting(true);
    try {
      await onImport({
        library_template_name: item.library_template_name,
        template_name: normalizeTemplateName(templateName),
        template_language: language.trim(),
        template_category: category,
        library_template_body_inputs: cleanBodyInputs(bodyInputs),
        library_template_button_inputs: cleanButtonInputs(buttonInputs),
        library_template_snapshot: templateLibrarySnapshot(item),
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {translate("crm.whatsapp.templates.import_title")}
          </DialogTitle>
        </DialogHeader>
        {item && (
          <div className="grid gap-4">
            <div className="rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-medium">{item.name}</div>
                <Badge variant="secondary">{item.category}</Badge>
                <Badge variant="outline">{item.language}</Badge>
              </div>
              {item.body_text && (
                <div className="mt-3 whitespace-pre-wrap text-sm">
                  {item.body_text}
                </div>
              )}
            </div>

            {duplicateTemplate && (
              <Alert variant="destructive">
                <AlertTriangle className="size-4" />
                <AlertTitle>
                  {translate("crm.whatsapp.templates.duplicate_name")}
                </AlertTitle>
                <AlertDescription>
                  {translate("crm.whatsapp.templates.duplicate_name_help")}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>{translate("crm.whatsapp.templates.name")}</Label>
                <Input
                  value={templateName}
                  onChange={(event) =>
                    setTemplateName(normalizeTemplateName(event.target.value))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{translate("crm.whatsapp.templates.language")}</Label>
                <Input
                  value={language}
                  onChange={(event) => setLanguage(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{translate("crm.whatsapp.templates.category")}</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTILITY">UTILITY</SelectItem>
                    <SelectItem value="MARKETING">MARKETING</SelectItem>
                    <SelectItem value="AUTHENTICATION">
                      AUTHENTICATION
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {buttonInputs.length > 0 && (
              <div className="grid gap-3 rounded-md border p-3">
                <div className="text-sm font-medium">
                  {translate("crm.whatsapp.templates.button_inputs")}
                </div>
                {buttonInputs.map((draft, index) => (
                  <LibraryButtonInputFields
                    key={`${draft.type}-${index}`}
                    draft={draft}
                    onChange={(nextDraft) =>
                      setButtonInputs((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? nextDraft : item,
                        ),
                      )
                    }
                  />
                ))}
              </div>
            )}

            <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
              <LibraryBodyInputOption
                checked={!!bodyInputs.addContactNumber}
                label={translate("crm.whatsapp.templates.add_contact_number")}
                onChange={(checked) =>
                  setBodyInputs((current) => ({
                    ...current,
                    addContactNumber: checked,
                  }))
                }
              />
              <LibraryBodyInputOption
                checked={!!bodyInputs.addLearnMoreLink}
                label={translate("crm.whatsapp.templates.add_learn_more")}
                onChange={(checked) =>
                  setBodyInputs((current) => ({
                    ...current,
                    addLearnMoreLink: checked,
                  }))
                }
              />
              <LibraryBodyInputOption
                checked={!!bodyInputs.addSecurityRecommendation}
                label={translate("crm.whatsapp.templates.add_security")}
                onChange={(checked) =>
                  setBodyInputs((current) => ({
                    ...current,
                    addSecurityRecommendation: checked,
                  }))
                }
              />
              <LibraryBodyInputOption
                checked={!!bodyInputs.addTrackPackageLink}
                label={translate("crm.whatsapp.templates.add_tracking")}
                onChange={(checked) =>
                  setBodyInputs((current) => ({
                    ...current,
                    addTrackPackageLink: checked,
                  }))
                }
              />
              <div className="space-y-2 sm:col-span-2">
                <Label>
                  {translate("crm.whatsapp.templates.code_expiration")}
                </Label>
                <Input
                  type="number"
                  min={0}
                  max={1440}
                  value={bodyInputs.codeExpirationMinutes ?? ""}
                  onChange={(event) =>
                    setBodyInputs((current) => ({
                      ...current,
                      codeExpirationMinutes: event.target.value
                        ? Number(event.target.value)
                        : undefined,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            disabled={
              !item ||
              !templateName.trim() ||
              !language.trim() ||
              !!duplicateTemplate ||
              importing
            }
            onClick={() => void submit()}
          >
            <Download className="size-4" />
            {translate("crm.whatsapp.templates.import")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LibraryBodyInputOption({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox
        checked={checked}
        onCheckedChange={(nextChecked) => onChange(nextChecked === true)}
      />
      {label}
    </label>
  );
}

function LibraryButtonInputFields({
  draft,
  onChange,
}: {
  draft: LibraryButtonInputDraft;
  onChange: (draft: LibraryButtonInputDraft) => void;
}) {
  const translate = useTranslate();
  const type = draft.type.toUpperCase();
  return (
    <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-[10rem_1fr]">
      <div>
        <div className="font-medium">{draft.text || type}</div>
        <div className="text-xs text-muted-foreground">{type}</div>
      </div>
      {type === "CALL" || type === "PHONE_NUMBER" ? (
        <div className="space-y-2">
          <Label>{translate("crm.whatsapp.templates.button_phone")}</Label>
          <Input
            value={draft.phoneNumber ?? ""}
            onChange={(event) =>
              onChange({ ...draft, phoneNumber: event.target.value })
            }
            placeholder="+15551234567"
          />
        </div>
      ) : type === "URL" ? (
        <div className="space-y-2">
          <Label>{translate("crm.whatsapp.templates.button_url")}</Label>
          <Input
            value={draft.url ?? ""}
            onChange={(event) =>
              onChange({ ...draft, url: event.target.value })
            }
            placeholder="https://echoflicks.ai/..."
          />
        </div>
      ) : type.includes("OTP") || type === "COPY_CODE" ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{translate("crm.whatsapp.templates.otp_type")}</Label>
            <Input
              value={draft.otpType ?? ""}
              onChange={(event) =>
                onChange({ ...draft, otpType: event.target.value })
              }
              placeholder="COPY_CODE"
            />
          </div>
          <LibraryBodyInputOption
            checked={!!draft.zeroTapTermsAccepted}
            label={translate("crm.whatsapp.templates.zero_tap_terms")}
            onChange={(checked) =>
              onChange({ ...draft, zeroTapTermsAccepted: checked })
            }
          />
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          {translate("crm.whatsapp.templates.button_no_inputs")}
        </div>
      )}
    </div>
  );
}

function TemplateDialog({
  open,
  draft,
  onOpenChange,
  onChange,
  onSave,
  onUploadMedia,
  mediaUploadEnabled,
}: {
  open: boolean;
  draft: WhatsAppTemplateInput;
  onOpenChange: (open: boolean) => void;
  onChange: (draft: WhatsAppTemplateInput) => void;
  onSave: (submit: boolean) => Promise<void>;
  onUploadMedia: (file: File) => Promise<WhatsAppTemplateMediaAsset>;
  mediaUploadEnabled: boolean;
}) {
  const translate = useTranslate();
  const notify = useNotify();
  const [uploading, setUploading] = useState(false);
  const bodyPlaceholders = getPlaceholderNumbers(draft.body_text);
  const headerPlaceholders = getPlaceholderNumbers(draft.header_text);
  const buttonDrafts = normalizeTemplateButtonDrafts(draft.buttons);
  const buttonErrors = validateTemplateButtons(buttonDrafts);
  const variableErrors = [
    templateVariableIssue(draft.body_text),
    draft.header_format === "TEXT"
      ? templateVariableIssue(draft.header_text, { maxVariables: 1 })
      : null,
  ].filter((error): error is string => Boolean(error));
  const mediaHeader = isMediaHeader(draft.header_format);
  const canSave =
    draft.name.trim() &&
    draft.language.trim() &&
    draft.body_text?.trim() &&
    !buttonErrors.length &&
    !variableErrors.length;
  const canSubmit =
    canSave &&
    (!mediaHeader || !!draft.header_handle) &&
    (!headerPlaceholders.length || !!draft.header_example?.trim()) &&
    bodyExamplesComplete(draft.body_examples, bodyPlaceholders.length);

  const uploadMedia = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const asset = await onUploadMedia(file);
      const targetHeaderFormat =
        asset.target_header_format && isMediaHeader(asset.target_header_format)
          ? asset.target_header_format
          : draft.header_format;
      onChange({
        ...draft,
        header_format: targetHeaderFormat ?? draft.header_format,
        header_handle: asset.header_handle || asset.sample_handle || "",
        header_media_asset_id: asset.id,
        header_media_url: asset.preview_url || draft.header_media_url,
      });
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{translate("crm.whatsapp.templates.new")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.templates.name")}</Label>
              <Input
                value={draft.name}
                onChange={(event) =>
                  onChange({
                    ...draft,
                    name: normalizeTemplateName(event.target.value),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.templates.language")}</Label>
              <Input
                value={draft.language}
                onChange={(event) =>
                  onChange({ ...draft, language: event.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.templates.category")}</Label>
              <Select
                value={draft.category}
                onValueChange={(category) => onChange({ ...draft, category })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTILITY">UTILITY</SelectItem>
                  <SelectItem value="MARKETING">MARKETING</SelectItem>
                  <SelectItem value="AUTHENTICATION">AUTHENTICATION</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div className="grid gap-2 sm:grid-cols-[12rem_1fr]">
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.templates.header")}</Label>
              <Select
                value={draft.header_format || "NONE"}
                onValueChange={(header_format) =>
                  onChange({
                    ...draft,
                    header_format,
                    header_handle: isMediaHeader(header_format)
                      ? draft.header_handle
                      : undefined,
                    header_media_asset_id: isMediaHeader(header_format)
                      ? draft.header_media_asset_id
                      : undefined,
                    header_media_url: isMediaHeader(header_format)
                      ? draft.header_media_url
                      : undefined,
                    header_text:
                      header_format === "TEXT" ? draft.header_text : "",
                  })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">NONE</SelectItem>
                  <SelectItem value="TEXT">TEXT</SelectItem>
                  <SelectItem value="IMAGE">IMAGE</SelectItem>
                  <SelectItem value="DOCUMENT">DOCUMENT</SelectItem>
                  <SelectItem value="VIDEO">VIDEO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{translate("crm.whatsapp.templates.header_text")}</Label>
              <Input
                value={draft.header_text}
                disabled={draft.header_format !== "TEXT"}
                onChange={(event) =>
                  onChange({ ...draft, header_text: event.target.value })
                }
              />
              {draft.header_format === "TEXT" && (
                <TemplateVariableGuide
                  label="Header"
                  maxVariables={1}
                  value={draft.header_text}
                />
              )}
            </div>
          </div>

          {mediaHeader && (
            <div className="grid gap-2 rounded-md border p-3">
              <Label>{translate("crm.whatsapp.templates.sample_media")}</Label>
              <Input
                type="file"
                accept={mediaAccept(draft.header_format)}
                disabled={!mediaUploadEnabled || uploading}
                onChange={(event) =>
                  void uploadMedia(event.target.files?.[0] ?? undefined)
                }
              />
              <div className="text-xs text-muted-foreground">
                {draft.header_handle
                  ? translate("crm.whatsapp.templates.sample_media_ready")
                  : mediaUploadEnabled && draft.header_format === "VIDEO"
                    ? translate(
                        "crm.whatsapp.templates.sample_media_video_help",
                      )
                    : mediaUploadEnabled
                      ? translate("crm.whatsapp.templates.sample_media_help")
                      : translate("crm.whatsapp.capabilities.media_disabled")}
              </div>
              <TemplateMediaPreview
                fileName={draft.header_media_asset_id}
                format={draft.header_format}
                url={draft.header_media_url}
              />
              {mediaUploadEnabled && (
                <div className="text-xs text-muted-foreground">
                  {translate("crm.whatsapp.templates.sample_media_limits")}
                </div>
              )}
            </div>
          )}

          {!!variableErrors.length && (
            <div className="grid gap-1">
              {variableErrors.map((error) => (
                <div className="text-xs text-destructive" key={error}>
                  {error}
                </div>
              ))}
            </div>
          )}

          {draft.header_format === "TEXT" && headerPlaceholders.length > 0 && (
            <div className="space-y-2">
              <Label>
                {translate("crm.whatsapp.templates.header_example")}
              </Label>
              <Input
                value={draft.header_example ?? ""}
                onChange={(event) =>
                  onChange({ ...draft, header_example: event.target.value })
                }
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>{translate("crm.whatsapp.templates.body")}</Label>
            <Textarea
              value={draft.body_text}
              onChange={(event) =>
                onChange({ ...draft, body_text: event.target.value })
              }
              rows={7}
            />
            <TemplateVariableGuide label="Body" value={draft.body_text} />
          </div>

          {bodyPlaceholders.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {bodyPlaceholders.map((placeholder, index) => (
                <div className="space-y-2" key={placeholder}>
                  <Label>
                    {translate("crm.whatsapp.templates.body_example", {
                      placeholder,
                    })}
                  </Label>
                  <Input
                    value={draft.body_examples?.[0]?.[index] ?? ""}
                    onChange={(event) =>
                      onChange({
                        ...draft,
                        body_examples: updateBodyExample(
                          draft.body_examples,
                          index,
                          event.target.value,
                        ),
                      })
                    }
                  />
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label>{translate("crm.whatsapp.templates.footer")}</Label>
            <Input
              value={draft.footer_text}
              onChange={(event) =>
                onChange({ ...draft, footer_text: event.target.value })
              }
            />
          </div>

          <TemplateButtonBuilder
            buttons={buttonDrafts}
            errors={buttonErrors}
            onChange={(buttons) => onChange({ ...draft, buttons })}
          />

          <WhatsAppTemplatePreview draft={draft} buttons={buttonDrafts} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            variant="outline"
            disabled={!canSave}
            onClick={() => void onSave(false)}
          >
            {translate("crm.whatsapp.templates.save_draft")}
          </Button>
          <Button
            disabled={!canSubmit || uploading}
            onClick={() => void onSave(true)}
          >
            {translate("crm.whatsapp.templates.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function statusVariant(status: string) {
  if (status === "rejected") return "destructive";
  if (status === "approved") return "default";
  return "secondary";
}

function templateRejectionReason(template: WhatsAppTemplate) {
  if (template.status !== "rejected") return null;
  const reason = template.rejection_reason?.trim();
  if (!reason || reason.toLowerCase() === "none") return null;
  return reason;
}

function shortSenderId(value?: string | null) {
  if (!value) return "-";
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function normalizeTemplateName(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9_]+/g, "_");
}

function isMediaHeader(value?: string | null) {
  return value === "IMAGE" || value === "DOCUMENT" || value === "VIDEO";
}

function mediaAccept(value?: string | null) {
  if (value === "IMAGE") return "image/jpeg,image/png";
  if (value === "VIDEO") return "video/mp4,video/3gpp,image/gif";
  if (value === "DOCUMENT") {
    return "application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  return undefined;
}

/**
 * One-click labels Felix is guaranteed to understand.
 *
 * Every preset is accepted on both of the bot's delivery paths, so picking one
 * takes the guesswork (including the slash question) out of the label entirely.
 */
function ButtonLabelPresetMenu({
  onPick,
}: {
  onPick: (label: string) => void;
}) {
  const translate = useTranslate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="h-6 px-2 text-xs"
          size="sm"
          type="button"
          variant="ghost"
        >
          <Sparkles className="size-3" />
          {translate("crm.whatsapp.buttons.use_suggested")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {BUTTON_LABEL_PRESETS.map((preset) => (
          <DropdownMenuItem
            key={preset.label}
            onSelect={() => onPick(preset.label)}
          >
            <div className="grid gap-0.5">
              <span className="font-medium">{preset.label}</span>
              <span className="text-xs text-muted-foreground">
                {translate(preset.descriptionKey)}
              </span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TemplateButtonBuilder({
  buttons,
  errors,
  onChange,
}: {
  buttons: TemplateButtonDraft[];
  errors: string[];
  onChange: (buttons: TemplateButtonDraft[]) => void;
}) {
  const translate = useTranslate();
  const quickReplyCount = buttons.filter(
    (button) => button.type === "QUICK_REPLY",
  ).length;
  const urlCount = buttons.filter((button) => button.type === "URL").length;
  const canAddQuickReply = buttons.length < 3 && quickReplyCount < 2;
  const canAddUrl = buttons.length < 3 && urlCount < 1;

  const updateButton = (index: number, button: TemplateButtonDraft) => {
    onChange(
      buttons.map((item, itemIndex) => (itemIndex === index ? button : item)),
    );
  };
  const removeButton = (index: number) => {
    onChange(buttons.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div className="grid gap-3 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">
            {translate("crm.whatsapp.templates.template_buttons")}
          </div>
          <div className="text-xs text-muted-foreground">
            {translate("crm.whatsapp.templates.template_buttons_help")}
          </div>
          <div className="text-xs text-muted-foreground">
            {translate("crm.whatsapp.templates.button_slash_help")}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            type="button"
            variant="outline"
            disabled={!canAddQuickReply}
            onClick={() =>
              onChange([
                ...buttons,
                {
                  type: "QUICK_REPLY",
                  text: nextQuickReplyButtonLabel(buttons),
                },
              ])
            }
          >
            <Plus className="size-4" />
            {translate("crm.whatsapp.templates.add_quick_reply")}
          </Button>
          <Button
            size="sm"
            type="button"
            variant="outline"
            disabled={!canAddUrl}
            onClick={() =>
              onChange([
                ...buttons,
                {
                  type: "URL",
                  text: "Watch examples",
                  url: "https://echoflicks.ai",
                },
              ])
            }
          >
            <ExternalLink className="size-4" />
            {translate("crm.whatsapp.templates.add_url_button")}
          </Button>
        </div>
      </div>

      {buttons.length ? (
        <div className="grid gap-3">
          {buttons.map((button, index) => (
            <div
              className="grid gap-3 rounded-md bg-muted/30 p-3 sm:grid-cols-[10rem_1fr_auto]"
              key={`${button.type}-${index}`}
            >
              <div className="space-y-2">
                <Label>{translate("crm.whatsapp.templates.button_type")}</Label>
                <Select
                  value={button.type}
                  onValueChange={(value) =>
                    updateButton(index, {
                      ...button,
                      type: value === "URL" ? "URL" : "QUICK_REPLY",
                      url: value === "URL" ? button.url : undefined,
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="QUICK_REPLY">
                      {translate("crm.whatsapp.templates.button_quick_reply")}
                    </SelectItem>
                    <SelectItem value="URL">
                      {translate("crm.whatsapp.templates.button_url_cta")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label>
                      {translate("crm.whatsapp.templates.button_text")}
                    </Label>
                    {button.type === "QUICK_REPLY" && (
                      <ButtonLabelPresetMenu
                        onPick={(label) =>
                          updateButton(index, { ...button, text: label })
                        }
                      />
                    )}
                  </div>
                  <Input
                    maxLength={25}
                    value={button.text}
                    onChange={(event) =>
                      updateButton(index, {
                        ...button,
                        text: event.target.value,
                      })
                    }
                    placeholder={
                      button.type === "QUICK_REPLY"
                        ? "Create my movie"
                        : "Watch examples"
                    }
                  />
                  {button.type === "QUICK_REPLY" && (
                    <ButtonIntentHint label={button.text} />
                  )}
                </div>
                {button.type === "URL" && (
                  <div className="space-y-2">
                    <Label>
                      {translate("crm.whatsapp.templates.button_url")}
                    </Label>
                    <Input
                      value={button.url ?? ""}
                      onChange={(event) =>
                        updateButton(index, {
                          ...button,
                          url: event.target.value,
                        })
                      }
                      placeholder="https://echoflicks.ai"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-end justify-end">
                <Button
                  aria-label={translate("crm.whatsapp.templates.remove_button")}
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={() => removeButton(index)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md bg-muted/30 p-3 text-sm text-muted-foreground">
          {translate("crm.whatsapp.templates.no_template_buttons")}
        </div>
      )}

      {errors.map((error) => (
        <div className="text-xs text-destructive" key={error}>
          {error}
        </div>
      ))}
    </div>
  );
}

function WhatsAppTemplatePreview({
  draft,
  buttons,
}: {
  draft: WhatsAppTemplateInput;
  buttons: TemplateButtonDraft[];
}) {
  const translate = useTranslate();
  const headerFormat = draft.header_format;
  const mediaHeader = isMediaHeader(headerFormat);
  const headerText =
    headerFormat === "TEXT"
      ? renderTemplatePreviewText(draft.header_text, "header", draft)
      : "";
  const bodyText = renderTemplatePreviewText(draft.body_text, "body", draft);
  const footerText = renderTemplatePreviewText(
    draft.footer_text,
    "footer",
    draft,
  );
  const previewButtons = cleanTemplateButtons(buttons) ?? [];

  return (
    <div className="grid gap-3 rounded-md border p-3">
      <div className="text-sm font-medium">
        {translate("crm.whatsapp.templates.preview")}
      </div>
      <WhatsAppMessageBubble
        body={bodyText}
        buttons={previewButtons}
        className="max-w-md"
        emptyBodyLabel={translate("crm.whatsapp.templates.preview_empty_body")}
        footer={footerText}
        header={headerText}
        media={
          mediaHeader
            ? {
                format: String(headerFormat ?? "").toUpperCase(),
                url: draft.header_media_url,
                label:
                  draft.header_media_asset_id != null
                    ? String(draft.header_media_asset_id)
                    : null,
              }
            : null
        }
      />
    </div>
  );
}

function TemplateMediaPreview({
  format,
  url,
  fileName,
}: {
  format?: string | null;
  url?: string | null;
  fileName?: string | number | null;
}) {
  const normalizedFormat = String(format ?? "").toUpperCase();
  const label = fileName ? String(fileName) : normalizedFormat || "Media";

  if (url && normalizedFormat === "IMAGE") {
    return (
      <img
        alt={label}
        className="aspect-video w-full rounded-md border object-cover"
        src={url}
      />
    );
  }

  if (url && normalizedFormat === "VIDEO") {
    return (
      <video
        className="aspect-video w-full rounded-md border object-cover"
        controls
        src={url}
      />
    );
  }

  if (url && normalizedFormat === "DOCUMENT") {
    return (
      <a
        className="flex min-h-16 items-center justify-between gap-3 rounded-md border bg-background px-3 py-2 text-sm"
        href={url}
        rel="noreferrer"
        target="_blank"
      >
        <span className="min-w-0 truncate">{label}</span>
        <ExternalLink className="size-4 shrink-0" />
      </a>
    );
  }

  return (
    <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed bg-background text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <ImageIcon className="size-4" />
        <span>{label}</span>
      </div>
    </div>
  );
}

function renderTemplatePreviewText(
  value: string | null | undefined,
  component: "header" | "body" | "footer",
  draft: WhatsAppTemplateInput,
) {
  const bodyExamples = draft.body_examples?.[0] ?? [];
  return String(value ?? "").replace(/\{\{\s*(\d+)\s*\}\}/g, (_, rawIndex) => {
    const index = Number(rawIndex);
    if (component === "header") {
      return draft.header_example?.trim() || `{{${index}}}`;
    }
    if (component === "body") {
      return bodyExamples[index - 1]?.trim() || `{{${index}}}`;
    }
    return `{{${index}}}`;
  });
}

function nextQuickReplyButtonLabel(buttons: TemplateButtonDraft[]) {
  const labels = new Set(
    buttons.map((button) => button.text.trim().toLowerCase()).filter(Boolean),
  );
  if (!labels.has("create my movie")) return "Create my movie";
  // "Stop promotions", not "Stop promos": the bot's consent keyword set is
  // {stop, unsubscribe, stop promotions}, and a quick-reply tap usually arrives
  // as plain text, so "Stop promos" would often fail to opt the recipient out.
  // See botButtonIntents.ts.
  if (!labels.has("stop promotions")) return "Stop promotions";
  return "";
}

function normalizeTemplateButtonDrafts(value: unknown): TemplateButtonDraft[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((button) => {
    const rawType = String(button.type ?? "QUICK_REPLY").toUpperCase();
    const type = rawType === "URL" ? "URL" : "QUICK_REPLY";
    return {
      type,
      text: String(button.text ?? ""),
      url: type === "URL" ? (stringOrUndefined(button.url) ?? "") : undefined,
    };
  });
}

function cleanTemplateButtons(
  value: unknown,
): TemplateButtonDraft[] | undefined {
  const buttons = normalizeTemplateButtonDrafts(value)
    .filter(templateButtonHasContent)
    .map((button) => ({
      type: button.type,
      text: button.text.trim(),
      url: button.type === "URL" ? button.url?.trim() : undefined,
    }));
  return buttons.length ? buttons : undefined;
}

function validateTemplateButtons(buttons: TemplateButtonDraft[]) {
  const activeButtons = buttons.filter(templateButtonHasContent);
  const errors: string[] = [];
  const quickReplies = activeButtons.filter(
    (button) => button.type === "QUICK_REPLY",
  );
  const urlButtons = activeButtons.filter((button) => button.type === "URL");
  const seenLabels = new Set<string>();

  if (activeButtons.length > 3) errors.push("Use at most three buttons.");
  if (quickReplies.length > 2) {
    errors.push("Use at most two quick reply buttons.");
  }
  if (urlButtons.length > 1) errors.push("Use at most one URL button.");

  activeButtons.forEach((button) => {
    const text = button.text.trim();
    if (!text) errors.push("Every button needs visible text.");
    if (text.length > 25) {
      errors.push("Button text must be 25 characters or less.");
    }
    const labelKey = text.toLowerCase();
    if (labelKey && seenLabels.has(labelKey)) {
      errors.push("Button labels must be unique.");
    }
    if (labelKey) seenLabels.add(labelKey);

    if (button.type === "URL") {
      const url = button.url?.trim() ?? "";
      if (!url) {
        errors.push("URL buttons need a destination URL.");
      } else if (!isHttpsStaticUrl(url)) {
        errors.push("URL buttons must use a static HTTPS URL.");
      }
    }
  });

  return Array.from(new Set(errors));
}

function templateButtonHasContent(button: TemplateButtonDraft) {
  return Boolean(button.text.trim() || button.url?.trim());
}

function isHttpsStaticUrl(value: string) {
  if (value.includes("{{") || value.includes("}}")) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:";
  } catch {
    return false;
  }
}

function TemplateVariableGuide({
  label,
  value,
  maxVariables,
}: {
  label: string;
  value?: string | null;
  maxVariables?: number;
}) {
  const tokens = parseTemplateVariableTokens(value);
  const placeholders = getPlaceholderNumbers(value);
  const issue = templateVariableIssue(value, { maxVariables });

  return (
    <div className="rounded-md border bg-muted/30 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{label} variables</span>
        <Badge variant="secondary" className="font-mono">
          {"{{1}}"}
        </Badge>
        <Badge variant="secondary" className="font-mono">
          {"{{2}}"}
        </Badge>
        <span className="text-muted-foreground">
          type placeholders exactly where values should be filled
        </span>
      </div>
      {value?.trim() ? (
        <div className="mt-2 whitespace-pre-wrap rounded-md bg-background px-3 py-2 font-mono leading-relaxed">
          {renderHighlightedTemplateText(value)}
        </div>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">
          {placeholders.length ? "Detected" : "No variables detected"}
        </span>
        {placeholders.map((placeholder) => (
          <Badge key={placeholder} variant="outline" className="font-mono">
            {`{{${placeholder}}}`}
          </Badge>
        ))}
        {tokens
          .filter((token) => !token.valid)
          .map((token) => (
            <Badge key={`${token.start}-${token.value}`} variant="destructive">
              {token.value}
            </Badge>
          ))}
      </div>
      {issue ? <div className="mt-2 text-destructive">{issue}</div> : null}
    </div>
  );
}

function renderHighlightedTemplateText(value: string) {
  const tokens = parseTemplateVariableTokens(value);
  if (!tokens.length) return value;

  const nodes: ReactNode[] = [];
  let cursor = 0;
  tokens.forEach((token) => {
    if (token.start > cursor) {
      nodes.push(value.slice(cursor, token.start));
    }
    nodes.push(
      <span
        className={
          token.valid
            ? "rounded bg-emerald-500/15 px-1 font-semibold text-emerald-700 dark:text-emerald-300"
            : "rounded bg-destructive/15 px-1 font-semibold text-destructive"
        }
        key={`${token.start}-${token.value}`}
      >
        {token.value}
      </span>,
    );
    cursor = token.end;
  });
  if (cursor < value.length) nodes.push(value.slice(cursor));
  return nodes;
}

type TemplateVariableToken = {
  value: string;
  start: number;
  end: number;
  valid: boolean;
};

function parseTemplateVariableTokens(
  value?: string | null,
): TemplateVariableToken[] {
  const text = value ?? "";
  const matches = text.matchAll(/\{\{[^{}\n]+?\}\}/g);
  return Array.from(matches).map((match) => {
    const raw = match[0];
    return {
      value: raw,
      start: match.index ?? 0,
      end: (match.index ?? 0) + raw.length,
      valid: /^\{\{\s*\d+\s*\}\}$/.test(raw),
    };
  });
}

function templateVariableIssue(
  value?: string | null,
  options: { maxVariables?: number } = {},
) {
  const tokens = parseTemplateVariableTokens(value);
  if (tokens.some((token) => !token.valid)) {
    return "Template variables must be numeric placeholders like {{1}}.";
  }

  const placeholders = getPlaceholderNumbers(value);
  if (
    options.maxVariables !== undefined &&
    placeholders.length > options.maxVariables
  ) {
    return `This field supports at most ${options.maxVariables} variable${
      options.maxVariables === 1 ? "" : "s"
    }.`;
  }
  if (
    placeholders.length &&
    placeholders.some((placeholder, index) => placeholder !== index + 1)
  ) {
    return "Template variables must be sequential starting at {{1}}.";
  }
  return null;
}

function renderIndustry(value: WhatsAppTemplateLibraryItem["industry"]) {
  if (Array.isArray(value)) return value.join(", ") || "-";
  return value || "-";
}

function cleanBodyInputs(
  value: WhatsAppTemplateLibraryBodyInputs,
): WhatsAppTemplateLibraryBodyInputs | undefined {
  const result: WhatsAppTemplateLibraryBodyInputs = {};
  for (const key of [
    "addContactNumber",
    "addLearnMoreLink",
    "addSecurityRecommendation",
    "addTrackPackageLink",
  ] as const) {
    if (value[key] === true) result[key] = true;
  }
  if (
    value.codeExpirationMinutes != null &&
    Number.isFinite(value.codeExpirationMinutes)
  ) {
    result.codeExpirationMinutes = value.codeExpirationMinutes;
  }
  return Object.keys(result).length ? result : undefined;
}

function cleanButtonInputs(
  drafts: LibraryButtonInputDraft[],
): WhatsAppTemplateLibraryButtonInput[] | undefined {
  const inputs = drafts
    .map((draft) => {
      const type = draft.type.toUpperCase();
      const input: WhatsAppTemplateLibraryButtonInput = { type };
      if (draft.phoneNumber?.trim()) {
        input.phoneNumber = draft.phoneNumber.trim();
      }
      if (draft.url?.trim()) {
        input.url = { url: draft.url.trim() };
      }
      if (draft.otpType?.trim()) {
        input.otpType = draft.otpType.trim();
      }
      if (draft.zeroTapTermsAccepted) {
        input.zeroTapTermsAccepted = true;
      }
      return Object.keys(input).length > 1 ? input : null;
    })
    .filter(Boolean) as WhatsAppTemplateLibraryButtonInput[];
  return inputs.length ? inputs : undefined;
}

function initialLibraryButtonDrafts(
  buttons: WhatsAppTemplateLibraryItem["buttons"],
): LibraryButtonInputDraft[] {
  if (!Array.isArray(buttons)) return [];
  return buttons.filter(isRecord).map((button) => ({
    type: String(button.type ?? "QUICK_REPLY"),
    text: stringOrUndefined(button.text),
    phoneNumber: stringOrUndefined(button.phoneNumber ?? button.phone_number),
    url: buttonUrlValue(button.url),
    otpType: stringOrUndefined(button.otpType ?? button.otp_type),
    zeroTapTermsAccepted: Boolean(
      button.zeroTapTermsAccepted ?? button.zero_tap_terms_accepted,
    ),
  }));
}

function makeTemplateNameSuggestion(
  item: WhatsAppTemplateLibraryItem,
  existingTemplates: WhatsAppTemplate[],
) {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const base = normalizeTemplateName(
    `echoflicks_${item.library_template_name || item.name}_${today}`,
  ).slice(0, 120);
  let candidate = base;
  let suffix = 2;
  while (
    existingTemplates.some(
      (template) =>
        template.name === candidate && template.language === item.language,
    )
  ) {
    candidate = `${base}_${suffix}`;
    suffix += 1;
  }
  return candidate;
}

function templateLibrarySnapshot(item: WhatsAppTemplateLibraryItem) {
  return {
    id: item.id,
    template_id: item.template_id,
    templateId: item.provider_template_id,
    library_template_name: item.library_template_name,
    templateName: item.name,
    name: item.name,
    language: item.language,
    category: item.category,
    body_text: item.body_text,
    templateBody: item.body_text,
    header_text: item.header_text,
    templateHeader: item.header_text,
    buttons: item.buttons,
    templateButtons: item.buttons,
    topic: item.topic,
    usecase: item.usecase,
    industry: item.industry,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringOrUndefined(value: unknown) {
  if (value == null || value === "") return undefined;
  return String(value);
}

function buttonUrlValue(value: unknown) {
  if (typeof value === "string") return value;
  if (!isRecord(value)) return undefined;
  const first = Object.values(value).find((item) => typeof item === "string");
  return stringOrUndefined(first);
}

function getPlaceholderNumbers(value?: string | null) {
  const matches = value?.match(/\{\{\s*\d+\s*\}\}/g) ?? [];
  return Array.from(
    new Set(
      matches
        .map((match) => Number(match.replace(/\D/g, "")))
        .filter((number) => Number.isFinite(number)),
    ),
  ).sort((left, right) => left - right);
}

function updateBodyExample(
  current: WhatsAppTemplateInput["body_examples"],
  index: number,
  value: string,
): string[][] {
  const next = [...(current?.[0] ?? [])];
  next[index] = value;
  return [next];
}

function bodyExamplesComplete(
  examples: WhatsAppTemplateInput["body_examples"],
  expectedCount: number,
) {
  if (!expectedCount) return true;
  const row = examples?.[0] ?? [];
  return row.length >= expectedCount && row.every((value) => value?.trim());
}

async function fileToBase64(file: File) {
  const buffer = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return window.btoa(binary);
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "crm.whatsapp.error";
}
